import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  FormControlLabel,
  Modal,
  Radio,
  RadioGroup,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';
import ActionButton from '../../../../components/ActionButton';
import SpinnerComponent from '../../../../components/SpinnerComponent';
import { etichettaRecapito } from './contattiNotificheUtils';

const useStyles = makeStyles((theme) => ({
  modal: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  contenitore: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    backgroundColor: 'white',
    color: 'black',
    padding: theme.spacing(4),
    boxShadow: theme.shadows[6],
    borderRadius: '6px',
    overflowY: 'auto',
    width: '90%',
    maxWidth: '620px',
    maxHeight: '90vh',
    textAlign: 'left',
  },
  nota: {
    color: theme.palette.text.secondary,
  },
  errore: {
    color: theme.palette.error.main,
    fontWeight: 600,
  },
  pulsanti: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  statoSalvataggio: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginRight: 'auto',
    color: theme.palette.text.secondary,
  },
  spinnerInline: { display: 'flex' },
}));

/**
 * Adding a recapito to one scope. It picks among the recapiti the esercizio
 * already has, on either scope, plus its responsabili sicurezza: a contatto is a
 * row of its own (BSDEVPLAT-125), and the anagrafica that creates one is not this
 * screen — there is no route here that creates a recapito, and offering a user
 * registration would turn a recipient into an account, which is exactly what the
 * model refuses. A responsabile chosen here is the *source of an address*: the
 * server writes a `contatto_notifica` row carrying it, which grants nothing.
 *
 * The same person on two scopes is one recapito addressed twice, so the picker
 * offers the ones missing from the target scope and nothing else.
 *
 * An empty picker has two causes and they read differently: every recapito of
 * this esercizio is already on this scope, or this esercizio has nothing to draw
 * a recapito from. The second is the state the escalation warning is about, and
 * telling that technician «sono già su questo scope» would be false.
 */
const AggiungiRecapitoModal = ({
  nessunRecapitoCensito,
  assegnabili,
  erroreServer,
  onAggiungi,
  onClose,
  open,
  salvataggioInCorso,
  titoloScope,
}) => {
  const classes = useStyles();
  const [sceltoId, setSceltoId] = useState('');

  const aggiungibile = Boolean(sceltoId) && !salvataggioInCorso;

  return (
    <Modal
      open={open}
      onClose={onClose}
      disableBackdropClick={salvataggioInCorso}
      disableEscapeKeyDown={salvataggioInCorso}
      className={classes.modal}
    >
      <div className={classes.contenitore} data-testid="aggiungi-recapito">
        <Typography variant="h5">Aggiungi un recapito</Typography>
        <Typography variant="body2">
          Il recapito scelto viene aggiunto allo scope <b>{titoloScope}</b> e da
          quel momento riceve le notifiche di quello scope.
        </Typography>

        {assegnabili.length === 0 && nessunRecapitoCensito && (
          <Typography variant="body2" className={classes.nota}>
            Questo esercizio non ha nessun recapito di notifica, e nessuno dei
            suoi responsabili sicurezza ha un indirizzo email: finché non ne
            esiste almeno uno, nessuno scope può avere destinatari e
            l’escalation resta senza. I responsabili sicurezza si censiscono
            dalla sezione «Responsabili sicurezza» del pannello di controllo;
            l’anagrafica dei contatti di notifica non è gestibile da qui.
          </Typography>
        )}

        {assegnabili.length === 0 && !nessunRecapitoCensito && (
          <Typography variant="body2" className={classes.nota}>
            Non ci sono altri recapiti da aggiungere: quelli noti a questo
            esercizio sono già su questo scope.
          </Typography>
        )}

        {assegnabili.length > 0 && (
          <RadioGroup
            aria-label="Recapito da aggiungere"
            value={sceltoId}
            onChange={(event) => setSceltoId(event.target.value)}
          >
            {assegnabili.map((contatto) => (
              <FormControlLabel
                key={contatto.id}
                value={String(contatto.id)}
                control={<Radio color="primary" />}
                label={etichettaRecapito(contatto)}
              />
            ))}
          </RadioGroup>
        )}

        {erroreServer && (
          <Typography variant="body2" className={classes.errore}>
            {erroreServer}
          </Typography>
        )}

        <div className={classes.pulsanti}>
          {/* Several seconds of silence — the write plus the re-read of the
              anagrafica that follows it — is exactly when whoever cannot see a
              spinner clicks again. */}
          {salvataggioInCorso && (
            <div className={classes.statoSalvataggio} role="status">
              <SpinnerComponent size={18} className={classes.spinnerInline} />
              <Typography variant="body2">Aggiunta in corso…</Typography>
            </div>
          )}
          <ActionButton
            secondaryButton
            label="Annulla"
            disabled={salvataggioInCorso}
            onClick={onClose}
          />
          <ActionButton
            label="Aggiungi"
            disabled={!aggiungibile}
            onClick={() => onAggiungi(sceltoId)}
          />
        </div>
      </div>
    </Modal>
  );
};

AggiungiRecapitoModal.propTypes = {
  // This esercizio has neither a recapito on any scope nor a responsabile
  // sicurezza with an address: an empty picker then means «there is nothing to
  // draw one from», not «all of them are already here».
  nessunRecapitoCensito: PropTypes.bool,
  assegnabili: PropTypes.arrayOf(PropTypes.object).isRequired,
  erroreServer: PropTypes.string,
  onAggiungi: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  salvataggioInCorso: PropTypes.bool,
  titoloScope: PropTypes.string.isRequired,
};

export default AggiungiRecapitoModal;
